import React, { useMemo } from "react";
import styled from "@emotion/styled";

/**
 * GitHub-contribution-style harvest grid: one cell per day, 7 rows (weekdays)
 * by ~53 columns (weeks), shaded by how long the machine actually harvested.
 *
 * Discrete cells rather than a line, because the data genuinely is one value
 * per day — a line between two days would draw a slope through time where
 * nothing was measured. It is also why the empty state is meaningful here:
 * "did not work that day" is a real answer, not missing data.
 *
 * Rendered as one <svg> of <rect>s. At ~370 cells this is far cheaper than the
 * equivalent in DOM nodes, and it stays crisp at the 4px cell size the device
 * row has space for.
 */

// Five steps, matching how GitHub buckets: one empty tone plus four filled.
// Green ramp so it reads as the same family as the sensor sparkline.
const EMPTY = "#EBEDF0";
const RAMP = ["#C6E48B", "#9BE05B", "#68AD18", "#3F6B0E"];

const Root = styled.svg`
  display: block;
  overflow: visible;
`;

export interface IActivityGridProps {
  /** Sparse map of "YYYY-MM-DD" -> working seconds. */
  days: Record<string, number>;
  /** How many days back the grid covers, ending today. */
  span?: number;
  /**
   * "calendar" is the GitHub shape — 7 weekday rows by however many weeks the
   * span covers. "strip" is a single row of `span` cells, oldest to newest, for
   * short windows where a calendar would degenerate into a two-column block
   * that reads as nothing.
   */
  layout?: "calendar" | "strip";
  cell?: number;
  gap?: number;
  onHoverDay?: (day: { day: string; seconds: number } | null) => void;
  className?: string;
}

const iso = (d: Date) => d.toISOString().slice(0, 10);

const ActivityGrid: React.FC<IActivityGridProps> = ({
  days,
  span = 365,
  layout = "calendar",
  cell = 4,
  gap = 1,
  onHoverDay,
  className,
}) => {
  const { cells, cols, rows, peak } = useMemo(() => {
    const today = new Date();
    // Align the last column to the current week so rows stay on fixed weekdays,
    // the way a calendar reads. Sunday = row 0, as GitHub does.
    const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    const start = new Date(end);
    start.setUTCDate(start.getUTCDate() - (span - 1));
    // The calendar shape keeps rows on fixed weekdays, so it starts on the
    // Sunday at or before the window; the strip starts exactly `span` days back.
    if (layout === "calendar") start.setUTCDate(start.getUTCDate() - start.getUTCDay());

    // Scale to the busiest day in view, so a light season still shows contrast
    // instead of a uniformly pale grid. A fixed scale would make every device
    // look identical in a slow year.
    const peak = Math.max(0, ...Object.values(days));

    const out: Array<{ day: string; seconds: number; col: number; row: number }> = [];
    const cursor = new Date(start);
    let col = 0;
    while (cursor <= end) {
      const key = iso(cursor);
      out.push({
        day: key,
        seconds: days[key] ?? 0,
        col: layout === "calendar" ? col : out.length,
        row: layout === "calendar" ? cursor.getUTCDay() : 0,
      });
      if (cursor.getUTCDay() === 6) col++;
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    return {
      cells: out,
      cols: layout === "calendar" ? col + 1 : out.length,
      rows: layout === "calendar" ? 7 : 1,
      peak,
    };
  }, [days, span, layout]);

  const shade = (seconds: number) => {
    if (seconds <= 0 || peak <= 0) return EMPTY;
    // Quartiles of the window's peak. Any recorded work gets at least the
    // lightest green — a 20-minute day must not read as "did not work".
    const q = Math.ceil((seconds / peak) * RAMP.length);
    return RAMP[Math.min(RAMP.length, Math.max(1, q)) - 1];
  };

  const step = cell + gap;
  const width = cols * step - gap;
  const height = rows * step - gap;

  return (
    <Root
      width={width}
      height={height}
      className={className}
      role="img"
      onMouseLeave={() => onHoverDay?.(null)}
    >
      {cells.map((c) => (
        <rect
          key={c.day}
          x={c.col * step}
          y={c.row * step}
          width={cell}
          height={cell}
          rx={1}
          fill={shade(c.seconds)}
          onMouseEnter={
            onHoverDay ? () => onHoverDay({ day: c.day, seconds: c.seconds }) : undefined
          }
        />
      ))}
    </Root>
  );
};

export default ActivityGrid;
